import { useLayoutEffect, useRef } from 'react'
import { SectionLabel } from '../../components/SectionLabel/SectionLabel'
import { SectionScaffold } from '../../components/SectionScaffold/SectionScaffold'
import { ProtectedImage } from '../../components/ProtectedImage/ProtectedImage'
import { assets } from '../../data/assets'
import { content } from '../../data/content'
import {
  createMotionMedia,
  gsap,
  initializeWhenNear,
  motionConditions,
  motionTokens,
  type MotionConditionState,
} from '../../motion'
import styles from './EngagementSection.module.css'

export function EngagementSection() {
  const sectionRef = useRef<HTMLDivElement>(null)
  const copy = content['SEC-004']
  const photographA = assets['AST-007']
  const photographB = assets['AST-008']
  const threadsA = assets['AST-009']
  const threadsB = assets['AST-010']

  useLayoutEffect(() => {
    const section = sectionRef.current
    if (!section) return

    return initializeWhenNear(section, () => {
      const media = createMotionMedia()

      media.add(motionConditions, (context) => {
        const { reduceMotion } = context.conditions as MotionConditionState
        const frames = Array.from(
          section.querySelectorAll<HTMLElement>('[data-gallery-reveal]'),
        )

        if (reduceMotion) {
          gsap.set(frames, { clearProps: 'all', clipPath: 'inset(0 0 0 0)' })
          return
        }

        frames.forEach((frame, index) => {
          gsap.fromTo(
            frame,
            { clipPath: 'inset(0 0 100% 0)' },
            {
              clipPath: 'inset(0 0 0% 0)',
              duration: motionTokens.duration.slow,
              delay: index % 2 === 1 ? 0.1 : 0,
              ease: motionTokens.ease.entrance,
              scrollTrigger: {
                trigger: frame,
                start: 'top 85%',
                once: true,
              },
            },
          )
        })
      })

      return () => media.revert()
    })
  }, [])

  return (
    <SectionScaffold
      id="SEC-004"
      className={styles.section}
      labelledBy="sec-004-title"
    >
      <div ref={sectionRef} className={styles.container}>
        <header className={styles.header}>
          <SectionLabel>— 04 / ENGAGEMENT &amp; UGC</SectionLabel>
          <h2 id="sec-004-title" className={styles.title}>
            {copy.title}
          </h2>
          <p className={styles.subtitle}>{copy.subtitle}</p>
          <p className={styles.intro}>{copy.body}</p>
        </header>

        <article className={`${styles.story} ${styles.storyGraffiti}`}>
          <div className={styles.collage}>
            <figure data-gallery-reveal className={styles.primaryFrame}>
              <ProtectedImage
                className={styles.storyImage}
                src={photographA.path}
                alt={photographA.alt}
                width={photographA.width}
                height={photographA.height}
                loading="lazy"
                decoding="async"
                draggable={false}
              />
            </figure>
            <figure data-gallery-reveal className={styles.detailFrame}>
              <ProtectedImage
                className={styles.storyImage}
                src={photographB.path}
                alt={photographB.alt}
                width={photographB.width}
                height={photographB.height}
                loading="lazy"
                decoding="async"
                draggable={false}
              />
            </figure>
          </div>
          <div className={styles.storyCopy}>
            <span className={styles.storyIndex} aria-hidden="true">
              01
            </span>
            <p>{copy.graffiti}</p>
          </div>
        </article>

        <article className={`${styles.story} ${styles.storyThreads}`}>
          <div className={styles.collage}>
            <figure data-gallery-reveal className={styles.primaryFrame}>
              <ProtectedImage
                className={`${styles.storyImage} ${styles.threadsPhoto}`}
                src={threadsA.path}
                alt={threadsA.alt}
                width={threadsA.width}
                height={threadsA.height}
                loading="lazy"
                decoding="async"
                draggable={false}
              />
            </figure>
            <figure data-gallery-reveal className={styles.detailFrame}>
              <ProtectedImage
                className={styles.storyImage}
                src={threadsB.path}
                alt={threadsB.alt}
                width={threadsB.width}
                height={threadsB.height}
                loading="lazy"
                decoding="async"
                draggable={false}
              />
            </figure>
          </div>
          <div className={styles.storyCopy}>
            <span className={styles.storyIndex} aria-hidden="true">
              02
            </span>
            <p>{copy.threads}</p>
            <p className={styles.data}>{copy.data}</p>
          </div>
        </article>
      </div>
    </SectionScaffold>
  )
}
